import { Component, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

// Shared pieces for the admin area. Everything here uses the same tokens as the rest of the
// signed-in app, so the admin pages look like one product with the site.

/** One section loads its own data: a failure in one never blanks the others. */
export function useLoad<T = any>(path: string) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const latest = useRef(0);
  const have = useRef(false);
  const reload = useCallback(() => {
    const mine = ++latest.current;       // only the newest answer is used
    setLoading(true);
    setError(null);
    return api.get(path)
      .then((r) => { if (mine === latest.current) { have.current = true; setData(r.data); } })
      .catch((e) => {
        if (mine !== latest.current) return;
        setError(apiError(e));
        // Something is already on screen: say it may be out of date instead of failing silently.
        if (have.current) toast.error("Couldn't refresh. What you see may be out of date.");
      })
      .finally(() => { if (mine === latest.current) setLoading(false); });
  }, [path]);
  useEffect(() => { reload(); }, [reload]);
  return { data, error, loading, reload };
}

/** If a section crashes while drawing, only that section says so. */
export class SectionBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) { console.error("Admin section failed", error); }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="rounded-3xl border border-border bg-card p-5" data-testid="admin-section-failed">
        <div className="text-sm font-bold">This part couldn't be shown.</div>
        <p className="mt-1 text-sm text-muted-foreground">
          The rest of the page still works.
        </p>
        <button type="button" onClick={() => this.setState({ failed: false })}
          className="mt-3 h-11 rounded-full border border-border px-5 text-sm font-semibold">
          Try again
        </button>
      </div>
    );
  }
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-5" role="alert">
      <div className="text-sm font-bold">Couldn't load this.</div>
      <p className="mt-1 text-sm text-muted-foreground">{message}</p>
      <button type="button" onClick={onRetry}
        className="mt-3 h-11 rounded-full border border-border px-5 text-sm font-semibold">
        Try again
      </button>
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-14 animate-pulse rounded-2xl bg-muted" />
      ))}
    </div>
  );
}

export function Kicker({ children }: { children: ReactNode }) {
  return (
    <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-primary">{children}</div>
  );
}

export function SectionTitle({ kicker, title, note }: { kicker?: string; title: string; note?: ReactNode }) {
  return (
    <div className="mb-3 mt-7 first:mt-0">
      {kicker && <Kicker>{kicker}</Kicker>}
      <h2 className="mt-1 text-base font-extrabold tracking-tight">{title}</h2>
      {note && <p className="mt-1 text-sm text-muted-foreground">{note}</p>}
    </div>
  );
}

const TONES = {
  good: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  watch: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  attention: "bg-destructive/10 text-destructive dark:text-red-400",
  neutral: "bg-muted text-muted-foreground",
  brand: "bg-primary/10 text-primary",
} as const;
export type Tone = keyof typeof TONES;

export function Pill({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONES[tone]}`}>
      {children}
    </span>
  );
}

/** A number is "—" when it is unknown; never a zero that looks like a real answer. */
export function show(value: any, suffix = ""): string {
  return value === null || value === undefined ? "—" : `${value}${suffix}`;
}

export function StatTile({ label, value, tone, children, testid }: {
  label: string; value: any; tone?: Tone; children?: ReactNode; testid?: string;
}) {
  const color = tone === "attention" ? "text-destructive dark:text-red-400"
    : tone === "watch" ? "text-amber-700 dark:text-amber-400" : "";
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <div className={`font-display text-2xl leading-none tracking-tight tabular-nums ${color}`}>
        {show(value)}
      </div>
      <div className="mt-2 text-xs leading-snug text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

/** A small trend line. No axes, no library: it only says "going up, flat, or going down". */
export function Sparkline({ values, label, className = "" }: {
  values: number[]; label: string; className?: string;
}) {
  const v = (values || []).map((n) => Number(n) || 0);
  if (v.length < 2) return null;
  const w = 120, h = 28, pad = 2;
  const max = Math.max(...v, 1);
  const step = (w - pad * 2) / (v.length - 1);
  const pts = v.map((n, i) => `${(pad + i * step).toFixed(1)},${(h - pad - (n / max) * (h - pad * 2)).toFixed(1)}`);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} preserveAspectRatio="none"
      className={`mt-3 h-7 w-full ${className}`}>
      <polyline points={pts.join(" ")} fill="none" stroke="currentColor" strokeWidth="1.75"
        strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Times arrive with their UTC offset; show them in the admin's own time. */
export function when(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—"
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export const FLAG_LABEL: Record<string, { text: string; tone: Tone }> = {
  waiting_for_you: { text: "Waiting for your OK", tone: "watch" },
  paused: { text: "Paused", tone: "attention" },
  deleting: { text: "Being deleted", tone: "neutral" },
  deletion_stuck: { text: "Deletion did not finish", tone: "attention" },
  was_removed: { text: "Removed before", tone: "attention" },
  texts_off: { text: "Texted STOP", tone: "attention" },
  no_number: { text: "No phone number", tone: "watch" },
  at_limit: { text: "Hit daily limit", tone: "watch" },
  reminders_failing: { text: "Reminders failing", tone: "attention" },
  replies_failing: { text: "Replies failing", tone: "attention" },
  google_needs_reconnect: { text: "Google needs reconnecting", tone: "watch" },
  gone_quiet: { text: "Gone quiet", tone: "watch" },
  never_started: { text: "Never started", tone: "neutral" },
};
